import React, { useState, useEffect, useCallback } from 'react';
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getBrands,
  createBrand,
  updateBrand,
  deleteBrand,
} from '../services/contentService';
import ImageUploader from './common/ImageUploader';
import { toast } from 'react-toastify';
import { SketchPicker } from 'react-color';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  AddCircleOutline,
  EditOutlined,
  DeleteOutline,
  BrokenImageOutlined,
  CategoryOutlined,
  BusinessOutlined,
  Add,
  ChevronRight,
} from '@mui/icons-material';

const AllProductsView = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const initialProductState = {
    productName: '',
    description: '',
    partNumber: '',
    price: '',
    categoryId: '',
    brandId: '',
    productImageUrl: '',
    cardAppearance: {
      backgroundColor: '#FFFFFF',
      textColor: '#000000',
      titleSize: 'medium',
      descriptionSize: 'small',
      priceColor: '#007BFF',
    },
  };

  const [currentProduct, setCurrentProduct] = useState(initialProductState);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);

  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [newBrandLogoUrl, setNewBrandLogoUrl] = useState('');
  const [editingBrand, setEditingBrand] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10000); // Keeping a high limit as per original file

  const loadProducts = useCallback(async (page, currentLimit = limit) => {
    setIsLoading(true);
    setError(null);
    try {
      const responseData = await getProducts({ page, limit: currentLimit });
      if (responseData && Array.isArray(responseData.products) && responseData.pagination) {
        setProducts(responseData.products);
        setTotalPages(responseData.pagination.totalPages || 1);
        setCurrentPage(responseData.pagination.page || page);
      } else {
        setProducts([]);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch products.');
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  const loadCategories = useCallback(async () => {
    try {
      const fetchedCategories = await getCategories();
      setCategories(fetchedCategories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError(prev => prev ? `${prev}\nFailed to fetch categories.` : 'Failed to fetch categories.');
    }
  }, []);

  const loadBrands = useCallback(async () => {
    try {
      const fetchedBrands = await getBrands();
      setBrands(fetchedBrands || []);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
      setError(prev => prev ? `${prev}\nFailed to fetch brands.` : 'Failed to fetch brands.');
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        await loadProducts(1, 10000);
        await loadCategories();
        await loadBrands();
      } catch (err) {
        const errorMessage = err.message || 'Failed to fetch initial data for All Products View.';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [loadProducts, loadCategories, loadBrands]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentProduct(prev => ({ ...prev, [name]: value }));
  };

  const handleCardAppearanceChange = (e) => {
    const { name, value } = e.target;
    setCurrentProduct(prev => ({
      ...prev,
      cardAppearance: { ...prev.cardAppearance, [name]: value },
    }));
  };

  const handleCardAppearanceColorPickerChange = (name, color) => {
    setCurrentProduct(prev => ({
      ...prev,
      cardAppearance: { ...prev.cardAppearance, [name]: color.hex },
    }));
  };

  const handleImageUploaded = useCallback((url) => {
    setCurrentProduct(prev => ({ ...prev, productImageUrl: url }));
  }, []);

  console.log('AllProductsView - type of handleImageUploaded:', typeof handleImageUploaded);
  console.log('AllProductsView - type of setNewBrandLogoUrl:', typeof setNewBrandLogoUrl);

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (isEditMode) {
        await updateProduct(currentProduct.id, currentProduct);
        toast.success('Product updated successfully!');
      } else {
        await createProduct(currentProduct);
        toast.success('Product created successfully!');
      }
      await loadProducts(1, 10000);
      setIsProductModalOpen(false);
      setCurrentProduct(initialProductState);
    } catch (err) {
      setError(err.message || 'Failed to save product.');
      toast.error(err.message || 'Failed to save product.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditProduct = (product) => {
    setCurrentProduct(product);
    setIsEditMode(true);
    setIsProductModalOpen(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      setIsLoading(true);
      try {
        await deleteProduct(productId);
        toast.success('Product deleted successfully!');
        await loadProducts(1, 10000);
      } catch (err) {
        setError(err.message || 'Failed to delete product.');
        toast.error(err.message || 'Failed to delete product.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, { name: newCategoryName });
        toast.success('Category updated successfully!');
      } else {
        await createCategory({ name: newCategoryName });
        toast.success('Category created successfully!');
      }
      await loadCategories();
      setIsCategoryModalOpen(false);
      setNewCategoryName('');
      setEditingCategory(null);
    } catch (err) {
      setError(err.message || 'Failed to save category.');
      toast.error(err.message || 'Failed to save category.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategoryName(category.name);
    setIsCategoryModalOpen(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      setIsLoading(true);
      try {
        await deleteCategory(categoryId);
        toast.success('Category deleted successfully!');
        await loadCategories();
      } catch (err) {
        setError(err.message || 'Failed to delete category.');
        toast.error(err.message || 'Failed to delete category.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBrandSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (editingBrand) {
        await updateBrand(editingBrand.id, { name: newBrandName, logoUrl: newBrandLogoUrl });
        toast.success('Brand updated successfully!');
      } else {
        await createBrand({ name: newBrandName, logoUrl: newBrandLogoUrl });
        toast.success('Brand created successfully!');
      }
      await loadBrands();
      setIsBrandModalOpen(false);
      setNewBrandName('');
      setNewBrandLogoUrl('');
      setEditingBrand(null);
    } catch (err) {
      setError(err.message || 'Failed to save brand.');
      toast.error(err.message || 'Failed to save brand.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditBrand = (brand) => {
    setEditingBrand(brand);
    setNewBrandName(brand.name);
    setNewBrandLogoUrl(brand.logoUrl);
    setIsBrandModalOpen(true);
  };

  const handleDeleteBrand = async (brandId) => {
    if (window.confirm('Are you sure you want to delete this brand?')) {
      setIsLoading(true);
      try {
        await deleteBrand(brandId);
        toast.success('Brand deleted successfully!');
        await loadBrands();
      } catch (err) {
        setError(err.message || 'Failed to delete brand.');
        toast.error(err.message || 'Failed to delete brand.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: '12px', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
          Product Management
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Manage your products, categories, and brands
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, color: 'info.main' }}>
            <CircularProgress size={20} sx={{ mr: 2 }} />
            <Typography>Loading...</Typography>
          </Box>
        )}

        {/* Products Section */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'medium' }}>
              Products
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={() => { setIsProductModalOpen(true); setIsEditMode(false); setCurrentProduct(initialProductState); }}
              sx={{
                backgroundColor: '#2196f3', '&:hover': { backgroundColor: '#1976d2' },
                borderRadius: '8px', padding: '10px 20px',
              }}
            >
              Add New Product
            </Button>
          </Box>

          <TableContainer component={Paper} elevation={1} sx={{ borderRadius: '8px' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8f8f8' }}>
                <TableRow>
                  <TableCell>Image</TableCell>
                  <TableCell>Product Details</TableCell>
                  <TableCell>Part Number</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.length === 0 && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No products found.
                    </TableCell>
                  </TableRow>
                )}
                {products.map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #eee',
                        }}
                      >
                        {product.productImageUrl ? (
                          <img
                            src={product.productImageUrl}
                            alt={product.productName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <BrokenImageOutlined sx={{ fontSize: 24, color: '#bdbdbd' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{product.productName}</Typography>
                      {product.description && (
                        <Tooltip title={product.description}>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '200px' }}>
                            {product.description}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{product.partNumber}</TableCell>
                    <TableCell>{product.price ? `$${parseFloat(product.price).toFixed(2)}` : 'N/A'}</TableCell>
                    <TableCell>
                      {categories.find(cat => cat.id === product.categoryId)?.name || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {brands.find(b => b.id === product.brandId)?.logoUrl && (
                          <img
                            src={brands.find(b => b.id === product.brandId).logoUrl}
                            alt="Brand Logo"
                            style={{ width: 20, height: 20, marginRight: 8, borderRadius: '50%', objectFit: 'cover' }}
                          />
                        )}
                        {brands.find(b => b.id === product.brandId)?.name || 'N/A'}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleEditProduct(product)} color="primary">
                        <EditOutlined />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteProduct(product.id)} color="error">
                        <DeleteOutline />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Categories Section */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'medium' }}>
              Categories
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={() => { setIsCategoryModalOpen(true); setEditingCategory(null); setNewCategoryName(''); }}
              sx={{
                backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#388E3C' },
                borderRadius: '8px', padding: '10px 20px',
              }}
            >
              Add New Category
            </Button>
          </Box>
          <Grid container spacing={3}>
            {categories.length === 0 && !isLoading && (
              <Grid item xs={12}>
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No categories found.
                </Typography>
              </Grid>
            )}
            {categories.map((category) => (
              <Grid item xs={12} sm={6} md={4} key={category.id}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CategoryOutlined sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="h6">{category.name}</Typography>
                  </Box>
                  <Box>
                    <IconButton onClick={() => handleEditCategory(category)} color="primary">
                      <EditOutlined />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteCategory(category.id)} color="error">
                      <DeleteOutline />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Brands Section */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'medium' }}>
              Brands
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={() => { setIsBrandModalOpen(true); setEditingBrand(null); setNewBrandName(''); setNewBrandLogoUrl(''); }}
              sx={{
                backgroundColor: '#9C27B0', '&:hover': { backgroundColor: '#7B1FA2' },
                borderRadius: '8px', padding: '10px 20px',
              }}
            >
              Add New Brand
            </Button>
          </Box>
          <Grid container spacing={3}>
            {brands.length === 0 && !isLoading && (
              <Grid item xs={12}>
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No brands found.
                </Typography>
              </Grid>
            )}
            {brands.map((brand) => (
              <Grid item xs={12} sm={6} md={4} key={brand.id}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {brand.logoUrl ? (
                      <img
                        src={brand.logoUrl}
                        alt={`${brand.name} Logo`}
                        style={{ width: 32, height: 32, marginRight: 12, borderRadius: '4px', objectFit: 'contain' }}
                      />
                    ) : (
                      <BusinessOutlined sx={{ mr: 1, color: 'text.secondary' }} />
                    )}
                    <Typography variant="h6">{brand.name}</Typography>
                  </Box>
                  <Box>
                    <IconButton onClick={() => handleEditBrand(brand)} color="primary">
                      <EditOutlined />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteBrand(brand.id)} color="error">
                      <DeleteOutline />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>

      {/* Product Modal */}
      <Dialog open={isProductModalOpen} onClose={() => { setIsProductModalOpen(false); setCurrentProduct(initialProductState); }} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Product' : 'Add New Product'}</DialogTitle>
        <DialogContent dividers>
          <form onSubmit={handleProductSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Product Name"
                  name="productName"
                  value={currentProduct.productName}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Part Number"
                  name="partNumber"
                  value={currentProduct.partNumber}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={currentProduct.description}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Price"
                  name="price"
                  value={currentProduct.price}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="categoryId"
                    value={currentProduct.categoryId}
                    label="Category"
                    onChange={handleInputChange}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Brand</InputLabel>
                  <Select
                    name="brandId"
                    value={currentProduct.brandId}
                    label="Brand"
                    onChange={handleInputChange}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {brands.map((brand) => (
                      <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Product Image</Typography>
                <ImageUploader onImageUploaded={handleImageUploaded} folderName="products" key={currentProduct.id || 'new-product-image-uploader'} />
                {currentProduct.productImageUrl && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                    <img
                      src={currentProduct.productImageUrl}
                      alt="Product Preview"
                      style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      {currentProduct.productImageUrl.split('/').pop()}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Card Appearance Settings</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" gutterBottom>Background Color</Typography>
                    <SketchPicker
                      color={currentProduct.cardAppearance.backgroundColor}
                      onChangeComplete={(color) => handleCardAppearanceColorPickerChange('backgroundColor', color)}
                      width="100%"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" gutterBottom>Text Color</Typography>
                    <SketchPicker
                      color={currentProduct.cardAppearance.textColor}
                      onChangeComplete={(color) => handleCardAppearanceColorPickerChange('textColor', color)}
                      width="100%"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" gutterBottom>Price Color</Typography>
                    <SketchPicker
                      color={currentProduct.cardAppearance.priceColor}
                      onChangeComplete={(color) => handleCardAppearanceColorPickerChange('priceColor', color)}
                      width="100%"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Title Size</InputLabel>
                      <Select
                        name="titleSize"
                        value={currentProduct.cardAppearance.titleSize}
                        label="Title Size"
                        onChange={handleCardAppearanceChange}
                      >
                        <MenuItem value="small">Small</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="large">Large</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Description Size</InputLabel>
                      <Select
                        name="descriptionSize"
                        value={currentProduct.cardAppearance.descriptionSize}
                        label="Description Size"
                        onChange={handleCardAppearanceChange}
                      >
                        <MenuItem value="small">Small</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="large">Large</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <input type="submit" hidden /> {/* Hidden submit to enable form submission on Enter */}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsProductModalOpen(false); setCurrentProduct(initialProductState); }} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleProductSubmit} disabled={isLoading} variant="contained" color="primary" startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Add />}>
            {isLoading ? 'Saving...' : (isEditMode ? 'Update Product' : 'Create Product')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={isCategoryModalOpen} onClose={() => { setIsCategoryModalOpen(false); setNewCategoryName(''); setEditingCategory(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Category Name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsCategoryModalOpen(false); setNewCategoryName(''); setEditingCategory(null); }} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleCategorySubmit} disabled={isLoading} variant="contained" color="primary" startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Add />}>
            {isLoading ? 'Saving...' : (editingCategory ? 'Update Category' : 'Create Category')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Brand Modal */}
      <Dialog open={isBrandModalOpen} onClose={() => { setIsBrandModalOpen(false); setNewBrandName(''); setNewBrandLogoUrl(''); setEditingBrand(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBrand ? 'Edit Brand' : 'Add New Brand'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Brand Name"
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>Brand Logo</Typography>
              <ImageUploader onImageUploaded={setNewBrandLogoUrl} folderName="brands" key={editingBrand?.id || 'new-brand-logo-uploader'} />
              {newBrandLogoUrl && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  <img
                    src={newBrandLogoUrl}
                    alt="Brand Logo Preview"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd' }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {newBrandLogoUrl.split('/').pop()}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsBrandModalOpen(false); setNewBrandName(''); setNewBrandLogoUrl(''); setEditingBrand(null); }} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleBrandSubmit} disabled={isLoading} variant="contained" color="primary" startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Add />}>
            {isLoading ? 'Saving...' : (editingBrand ? 'Update Brand' : 'Create Brand')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllProductsView;